"use client"

import {
    getPwaInstallPlatform,
    hasHandledPwaInstallNotice,
    isPwaStandalone,
    markPwaInstallNoticeDismissed,
    markPwaInstalled,
    type PwaInstallPlatform,
} from "@/shared/lib/pwa-install"
import { useEffect, useState } from "react"

type BeforeInstallPromptEvent = Event & {
    prompt: () => Promise<void>
    userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>
}

type InstallMethod = PwaInstallPlatform | null

function isIosSafari() {
    const userAgent = window.navigator.userAgent.toLowerCase()
    const isIOS = /iphone|ipad|ipod/.test(userAgent) || (userAgent.includes("mac") && window.navigator.maxTouchPoints > 1)
    const isSafari = /safari/.test(userAgent) && !/crios|fxios|edgios|chrome|android/.test(userAgent)

    return isIOS && isSafari
}

/**
 * @returns PWA install state and install action for supported platforms.
 */
export function usePwaInstall() {
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
    const [isInstallable, setIsInstallable] = useState(false)
    const [installMethod, setInstallMethod] = useState<InstallMethod>(null)

    useEffect(() => {
        const platform = getPwaInstallPlatform()

        if (isPwaStandalone()) {
            markPwaInstalled(platform)
            return
        }

        if (hasHandledPwaInstallNotice(platform)) return

        function handleBeforeInstallPrompt(event: Event) {
            event.preventDefault()
            setDeferredPrompt(event as BeforeInstallPromptEvent)
            setInstallMethod(platform === "android" ? "android" : "web")
            setIsInstallable(true)
        }

        function handleAppInstalled() {
            markPwaInstalled(platform)
            setDeferredPrompt(null)
            setInstallMethod(null)
            setIsInstallable(false)
        }

        if (platform === "ios" && isIosSafari()) {
            setInstallMethod("ios")
            setIsInstallable(true)
        }

        window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt)
        window.addEventListener("appinstalled", handleAppInstalled)

        return () => {
            window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt)
            window.removeEventListener("appinstalled", handleAppInstalled)
        }
    }, [])

    async function install() {
        if ((installMethod !== "android" && installMethod !== "web") || !deferredPrompt) return false

        await deferredPrompt.prompt()
        const choiceResult = await deferredPrompt.userChoice
        const wasAccepted = choiceResult.outcome === "accepted"

        if (wasAccepted) {
            markPwaInstalled(installMethod)
            setIsInstallable(false)
        } else {
            markPwaInstallNoticeDismissed(installMethod)
        }

        setDeferredPrompt(null)
        return wasAccepted
    }

    return { isInstallable, install, installMethod }
}
